/**
 * Router Invalidation for TanStack Router
 *
 * Registers router instance for invalidation after auth state changes.
 * Solves the "stale closure" problem where React Context updates
 * don't automatically trigger TanStack Router to re-evaluate guards.
 *
 * @module router/routerInvalidation
 */

/** @type {import('@tanstack/react-router').AnyRouter | null} */
let routerInstance = null;

/**
 * Register router instance for invalidation after auth changes.
 * Call this once in your app's router setup.
 *
 * @param {import('@tanstack/react-router').AnyRouter} router - TanStack Router instance
 * @returns {void}
 *
 * @example
 * ```js
 * import { registerRouter } from '@bb/session-manager';
 * import { router } from './router';
 *
 * registerRouter(router);
 * ```
 */
export function registerRouter(router) {
  routerInstance = router;
}

/**
 * Force router to re-evaluate all beforeLoad guards.
 * Call after login, logout, or permission changes.
 *
 * @returns {void}
 *
 * @example
 * ```js
 * import { invalidateRouter } from '@bb/session-manager';
 *
 * // In your login handler:
 * async function handleLogin() {
 *   await auth0.loginWithRedirect();
 *   invalidateRouter(); // Force route guards to re-run
 * }
 * ```
 */
export function invalidateRouter() {
  if (routerInstance) {
    routerInstance.invalidate();
  }
}

/**
 * Get the registered router instance (for testing/debugging).
 *
 * @returns {import('@tanstack/react-router').AnyRouter | null}
 */
export function getRouterInstance() {
  return routerInstance;
}

/**
 * Clear the registered router instance (for testing).
 *
 * @returns {void}
 */
export function clearRouterInstance() {
  routerInstance = null;
}
